// KÖZÖS csatolmány-panel: lista (fájlnév + presigned letöltés-link + feltöltő + dátum) + feltöltés + törlés.
//
// MIÉRT `shared/`: KÉT vertikum használja — a ticket-részletező és az üzenet-részletnézet —, és a panel
// viselkedése (kliens-validáció, sorban-feltöltés, megerősített törlés, üres/betöltő állapot) mindkettőnél
// UGYANAZ. Ami eltér, az csak (a) a KÉT végpont-hívás és (b) a SZÖVEG. Az előbbi callbackként, az utóbbi
// FELOLDOTT stringekként jön a hívótól — nem i18n-kulcs-prefixként: a típusos i18n-kulcsokkal a dinamikus
// prefix elveszti a típus-ellenőrzést, és a két vertikum szóhasználata sem feltétlenül azonos.
//
// A bájtok SOSEM mennek át a BFF-en: a feltöltés presigned PUT-tal közvetlenül a tárba, a letöltés presigned
// GET-URL-lel közvetlenül a tárból. A presigned URL SOHA nem logolt.
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  PaperClipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { useAttachmentUploader } from '@/hooks/use-attachment-uploader';
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  type AttachmentUploadOutcome,
} from '@/lib/attachment-rules';
import { downloadFresh } from '@/lib/fresh-download';

/** A panel által megjelenített sor — a ticket- és az üzenet-melléklet KÖZÖS részhalmaza. */
export interface AttachmentPanelRow {
  id: string;
  filename: string;
  downloadUrl: string | null;
  uploadedByName?: string | null | undefined;
  /** A feltöltő identity-je — ha VAN id, de a név nem oldható fel, a meta-sor „Törölt felhasználó"-t mutat. */
  uploadedById?: string | null | undefined;
  createdAt: string | null;
}

/** A törlés kimenete (a hívó végpont-hívásából) — a panel ebből választ toastot. */
export type AttachmentDeleteOutcome = 'ok' | 'forbidden' | 'unavailable' | 'failed';

/** FELOLDOTT feliratok (a hívó t()-eli literál kulcsokkal → megmarad a típus-ellenőrzés). */
export interface AttachmentPanelLabels {
  title: string;
  upload: string;
  uploading: string;
  empty: string;
  download: string;
  remove: string;
  removing: string;
  removeTitle: string;
  /** A törlés-megerősítés szövege a fájlnévvel. */
  removeDescription: (filename: string) => string;
  cancel: string;
  limitReached: string;
  /**
   * A TARTALOM NÉLKÜLI (migrált) melléklet magyarázata. Ilyen sor a legacy-ből jött át: a metaadat megvan, a
   * BÁJTOK nem (azok a régi szerver fájlrendszerén élnek, a dumpban nincsenek) — a fájl-átköltöztetés
   * cutover-tétel. Egy néma, kattinthatatlan sor „elromlott felület"-nek látszik; ez a felirat kimondja, mi van.
   */
  unavailable: string;
}

/** FELOLDOTT toast-üzenetek. */
export interface AttachmentPanelMessages {
  limit: string;
  invalidType: string;
  tooLarge: string;
  uploaded: string;
  uploadFailed: string;
  forbidden: string;
  validation: string;
  unavailable: string;
  deleted: string;
}

export interface AttachmentPanelProps {
  rows: AttachmentPanelRow[];
  loading: boolean;
  /** Feltöltés/törlés joga — enélkül csak olvasható a lista. */
  canManage: boolean;
  labels: AttachmentPanelLabels;
  messages: AttachmentPanelMessages;
  /** Egy fájl feltöltése (a hívó feature-specifikus, háromlépéses folyamata). */
  onUpload: (file: File) => Promise<AttachmentUploadOutcome>;
  /** Egy melléklet törlése. */
  onDelete: (id: string) => Promise<AttachmentDeleteOutcome>;
  /** Sikeres feltöltés/törlés után — a hívó invalidálja a saját cache-kulcsait. */
  onChanged: () => void;
  /**
   * KATTINTÁSKOR KÉRT friss letöltő-URL (Évi #79): a sorban kapott presigned URL percek alatt lejár, a lap
   * viszont tovább nyitva marad. Megadva a link kattintásra ezt hívja, és a friss URL-re navigál; `failed` a
   * toast, ha nem kapott URL-t. Elhagyva a sor `downloadUrl`-je a link (a korábbi viselkedés).
   */
  freshDownload?: { resolve: (id: string) => Promise<string | null>; failed: string };
  /** Dátum-formázó (a hívó locale-ja) — a panel nem dönt formátumot. */
  formatDate: (iso: string | null) => string;
  /** Elhagyható extra osztály a szekción (pl. sheet-en belül nincs kártya-keret). */
  className?: string;
  /**
   * A FEJLÉC tipográfiája:
   *  • `card` (alap) — ikon + `itemTitle` cím + darabszám: a kártyás/sheetes kontextus.
   *  • `section` — ikon NÉLKÜL, `overline` cím: a FORM-/DETAIL-oldalak szekció-nyelve (`FormSection`).
   * MIÉRT prop és nem külön komponens: a panel VISELKEDÉSE (feltöltés/törlés/limit/hiba) azonos — csak a
   * fejléc-tipográfia illeszkedik a hordozó felülethez. Két komponens itt copy-paste lenne.
   */
  headerVariant?: 'card' | 'section';
  /**
   * A FEJLÉC-CÍM (ikon + cím + darabszám) elhagyása. A feltöltő-gomb ilyenkor is megjelenik (ha van joga a
   * hívónak) — csak a cím-blokk marad el. Ott kell, ahol a hordozó felület MÁR megnevezte a szekciót, vagy
   * ahol a lista önmagában beszédes: a BEJÖVŐ üzenet részlet-panelén a „Mellékletek 1" külön kiírása zaj volt
   * (user-döntés) — a fájl-sorok maguk hordozzák a nevet, feltöltőt és dátumot.
   */
  hideHeader?: boolean;
  /** Horgony a ragadós szekció-navigációhoz (a `FormSection` `id`-jének párja) — a detail-oldalak railjéhez. */
  id?: string;
}

export function AttachmentPanel({
  rows,
  loading,
  canManage,
  labels,
  messages,
  onUpload,
  onDelete,
  onChanged,
  formatDate,
  freshDownload,
  className = 'card-glass-lg space-y-4 rounded-xl p-5',
  headerVariant = 'card',
  hideHeader = false,
  id,
}: AttachmentPanelProps): JSX.Element {
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<AttachmentPanelRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  /** A letöltő-link kattintása: friss URL-lel, ha a hívó adott feloldót (különben a sor URL-je marad a link). */
  const onDownloadClick = (e: MouseEvent<HTMLAnchorElement>, attachmentId: string): void => {
    if (!freshDownload) return;
    e.preventDefault();
    void downloadFresh(() => freshDownload.resolve(attachmentId)).then((ok) => {
      if (!ok) toast.error(freshDownload.failed);
    });
  };

  const { busy, inputRef, onFiles } = useAttachmentUploader({
    existingCount: rows.length,
    onUpload,
    onChanged,
    messages,
  });

  const onConfirmDelete = (): void => {
    if (!deleteTarget) return;
    setDeleting(true);
    void (async () => {
      try {
        const outcome = await onDelete(deleteTarget.id);
        if (outcome === 'ok') {
          toast.success(messages.deleted);
          onChanged();
        } else if (outcome === 'forbidden') {
          toast.error(messages.forbidden);
        } else if (outcome === 'unavailable') {
          toast.error(messages.unavailable);
        } else {
          toast.error(messages.uploadFailed);
        }
      } catch {
        toast.error(messages.uploadFailed);
      } finally {
        setDeleting(false);
        setDeleteTarget(null);
      }
    })();
  };

  const atLimit = rows.length >= MAX_ATTACHMENTS;

  return (
    <section {...(id ? { id, tabIndex: -1 } : {})} className={className}>
      <div className="flex flex-wrap items-center gap-2">
        {!hideHeader && (
          <>
            {headerVariant === 'card' && <DocumentIcon className="text-muted-foreground size-5" />}
            <Text variant={headerVariant === 'card' ? 'itemTitle' : 'overline'} as="h2">
              {labels.title}
            </Text>
            <Text variant="secondary" as="span">
              {rows.length}
            </Text>
          </>
        )}
        {canManage && (
          <div className="ml-auto">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={busy || atLimit}
              title={atLimit ? labels.limitReached : undefined}
            >
              {/* GEMKAPOCS, nem feltöltés-nyíl (user-döntés, 2026-09-09): a rendszerben a csatolás jele
                  mindenhol a gemkapocs — a jegy-nyitó és az üzenet-írás `PendingAttachmentsField`-je is azt
                  használja, a felirat pedig már egységesen „Fájl csatolása". Egy felület, ahol ugyanaz a
                  művelet MÁS ikont visel, azt tanítja, hogy a két gomb nem ugyanaz. */}
              <PaperClipIcon />
              {busy ? labels.uploading : labels.upload}
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                if (e.target.files) void onFiles(e.target.files);
              }}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <Text variant="secondary" as="p">
          {labels.empty}
        </Text>
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-2">
              <DocumentIcon className="text-muted-foreground size-5 shrink-0" />
              <div className="min-w-0 flex-1">
                {a.downloadUrl ? (
                  <a
                    href={a.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    download={a.filename}
                    onClick={(e) => onDownloadClick(e, a.id)}
                    className="text-brand-hover block truncate text-sm font-medium hover:underline"
                  >
                    {a.filename}
                  </a>
                ) : (
                  // NINCS letölthető tartalom (migrált sor: a bájtok nem jöttek át) → NEM link, és a MÁSODIK
                  // sor megmondja, miért. Egy néma, kattinthatatlan fájlnév „elromlott felület"-nek látszik.
                  <Text variant="body" as="span" className="truncate">
                    {a.filename}
                  </Text>
                )}
                <Text variant="secondary" as="div" className="truncate">
                  {[
                    // Feloldatlan, de LÉTEZŐ feltöltő-id = törölt személy — nem tűnik el némán a meta-sorból.
                    a.uploadedByName ?? (a.uploadedById ? t('common.deletedUser') : null),
                    formatDate(a.createdAt),
                    a.downloadUrl === null ? labels.unavailable : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </div>
              {a.downloadUrl && (
                <a
                  href={a.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  download={a.filename}
                  onClick={(e) => onDownloadClick(e, a.id)}
                  aria-label={labels.download}
                  title={labels.download}
                  className="text-muted-foreground hover:text-foreground rounded-md p-1.5 transition-colors"
                >
                  <ArrowDownTrayIcon className="size-4" />
                </a>
              )}
              {canManage && (
                <button
                  type="button"
                  onClick={() => setDeleteTarget(a)}
                  aria-label={labels.remove}
                  title={labels.remove}
                  className="text-muted-foreground hover:text-destructive rounded-md p-1.5 transition-colors"
                >
                  <TrashIcon className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={labels.removeTitle}
        description={labels.removeDescription(deleteTarget?.filename ?? '')}
        confirmLabel={labels.remove}
        cancelLabel={labels.cancel}
        pendingLabel={labels.removing}
        destructive
        pending={deleting}
        onConfirm={onConfirmDelete}
      />
    </section>
  );
}
